"use client";

import {
  createCalendar,
  getLocalTimeZone,
  parseZonedDateTime,
  toCalendarDateTime,
  today,
  toTime,
  toZoned,
} from "@internationalized/date";
import * as RadixDialog from "@radix-ui/react-dialog";
import { useTranslations } from "next-intl";
import { useSearchParams } from "next/navigation";
import { useState, type ReactNode } from "react";
import { useCalendar, useLocale } from "react-aria";
import { useCalendarState } from "react-stately";

import { CloseIcon } from "~/components/icons/close-icon";
import { Viewport } from "~/components/sm";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
} from "~/components/ui/dialog";
import { useTrack } from "~/lib/use-track";

import { DesktopBookMeetingContent } from "./book-meeting-desktop";
import { MobileBookMeetingContent } from "./book-meeting-mobile";

export function useMeeting() {
  const searchParams = useSearchParams();
  const searchParamsDefault = searchParams.get("slot");
  const timeZone = getLocalTimeZone();
  const tomorrow = today(timeZone).add({ days: 1 });
  const state = useCalendarState({
    locale: useLocale().locale,
    createCalendar,
    minValue: tomorrow,
    defaultValue: searchParamsDefault
      ? parseZonedDateTime(searchParamsDefault)
      : tomorrow,
  });
  const calendar = useCalendar({}, state);

  // Get an array of time slots, calendar working hours
  const timeSlots = Array.from({ length: 24 })
    .flatMap((_, index) => {
      const time = toTime(
        toCalendarDateTime(state.value ?? tomorrow).set({ hour: index }),
      );
      return [time, time.set({ minute: 30 })];
    })
    .slice(18, 45);
  const [timeSlot, setTimeSlot] = useState(timeSlots[6]!);
  const zonedDateTime = toZoned(state.value ?? tomorrow, timeZone).set({
    hour: timeSlot.hour,
    minute: timeSlot.minute,
  });
  return { calendar, state, zonedDateTime, setTimeSlot, timeSlots };
}

export function BookMeeting({ children }: { children?: ReactNode }) {
  const { calendar, setTimeSlot, state, timeSlots, zonedDateTime } =
    useMeeting();
  const track = useTrack();
  const t = useTranslations("BookMeeting");
  return (
    <Viewport
      breakpoint="md"
      mobile={
        <RadixDialog.Root
          modal
          onOpenChange={(open) => {
            if (open) {
              track({ name: "Open Consultation" });
            }
          }}
        >
          <RadixDialog.Trigger asChild>
            {children ?? (
              <Button variant="sunset">{t("bookMeetingCTA")}</Button>
            )}
          </RadixDialog.Trigger>
          <RadixDialog.Portal>
            <RadixDialog.Overlay className="pointer-events-none! fixed inset-0 z-40 bg-neutral-200">
              <RadixDialog.Content className="h-screen overflow-y-auto p-5">
                <RadixDialog.Close asChild>
                  <Button
                    className="border-prune-500 text-prune-500 border p-1"
                    size="circle"
                    variant="ghost"
                  >
                    <CloseIcon />
                  </Button>
                </RadixDialog.Close>
                <MobileBookMeetingContent
                  calendar={calendar}
                  setTimeSlot={setTimeSlot}
                  state={state}
                  timeSlots={timeSlots}
                  zonedDateTime={zonedDateTime}
                />
              </RadixDialog.Content>
            </RadixDialog.Overlay>
          </RadixDialog.Portal>
        </RadixDialog.Root>
      }
      desktop={
        <Dialog
          onOpenChange={(open) => {
            if (open) {
              track({ name: "Open Consultation" });
            }
          }}
        >
          <DialogTrigger asChild>
            {children ?? (
              <Button variant="sunset">{t("bookMeetingCTA")}</Button>
            )}
          </DialogTrigger>
          <DialogContent>
            <DialogTitle className="mb-8 text-center text-xl font-medium">
              {t("title")}
            </DialogTitle>
            <DesktopBookMeetingContent
              calendar={calendar}
              state={state}
              zonedDateTime={zonedDateTime}
              setTimeSlot={setTimeSlot}
              timeSlots={timeSlots}
            />
          </DialogContent>
        </Dialog>
      }
    />
  );
}
